import { keyframes, SxProps, Theme } from "@mui/material";
import { Position } from "reactflow";
import { EDGE_COLORS, Layouts } from "../helpers/flow.constants";

// Define the expand animation
const expandAnimation = keyframes`
    0% { transform: scale(1); }
    25% { transform: scale(0.75); }
    100% { transform: scale(1); }
`;

// Selected node styles - centralized configuration
export const selectedNodeStyles = {
    backgroundColor: "#D8E4F0",
    borderColor: "#A7B7C7",
    scale: 1.2
} as const;

// Default node styles - centralized configuration
export const defaultNodeStyles = {
    borderColor: "#D8E4F0",
    hoverBorderColor: "#8091A3",
    scale: 1.1,
    hoverScale: 1.2
} as const;

const getBgColor = (selected: boolean, invalid: boolean = false) => {
    if (invalid) return "#FAEDEF";
    if (selected) return selectedNodeStyles.backgroundColor;
    return "white";
};

const getBorderStyles = (selected: boolean, invalid: boolean = false) => {
    if (invalid) return `1px solid #DB2A4A`;
    if (selected) return `1px solid ${selected ? selectedNodeStyles.borderColor : defaultNodeStyles.borderColor}`;
    return `1px solid ${defaultNodeStyles.borderColor}`;
};

export const getNodeContainerStyles = (
    selected: boolean,
    invalid: boolean = false,
    sourcePosition: Position
): SxProps<Theme> => ({
    position: "relative",
    backgroundColor: getBgColor(selected, invalid),
    borderRadius: "16px",
    padding: "4px 8px",
    border: getBorderStyles(selected, invalid),
    transition: "all 0.2s ease-in-out",
    // transform: selected ? `scale(${selectedNodeStyles.scale})` : `scale(${defaultNodeStyles.scale})`,
    "&:hover": {
        // transform: selected ? `scale(${selectedNodeStyles.scale})` : `scale(${defaultNodeStyles.hoverScale})`,
        borderColor: selected ? selectedNodeStyles.borderColor : defaultNodeStyles.hoverBorderColor,
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        "& .target-handle": {
            ...(sourcePosition === Position.Bottom
                ? { borderTopColor: selectedNodeStyles.borderColor }
                : { borderLeftColor: selectedNodeStyles.borderColor })
        }
    },
    width: "240px",
    height: "60px",
    animation: `${expandAnimation} 0.2s ease-in`
});

// Keep the old export for backward compatibility
export const nodeContainerStyles: SxProps<Theme> = getNodeContainerStyles(false);

export const getColors = type => {
    switch (type) {
        case "SOURCE":
            return "#346CC2";
        case "TARGET":
            return "#2E8071";
        default:
            return "#6E7D8C";
    }
};

export const categoryLabelStyles: (boolean, string) => SxProps<Theme> = (isRunning: boolean = false, type: string) => {
    return {
        position: "absolute",
        ...(type === "TARGET" ? { bottom: "-20px" } : { top: "-20px" }),
        left: "10px",
        padding: "1px 8px",
        color: isRunning ? "greyscale.50" : "greyscale.700",
        borderRadius: type === "TARGET" ? "0 0 6px 6px" : "6px 6px 0 0",
        background: isRunning ? getColors(type) : "rgba(110, 125, 140, 0.10)"
    };
};

export const nodeContentStyles: SxProps<Theme> = {
    display: "flex",
    alignItems: "center",
    gap: 1 / 2,
    height: "100%"
};

export const nodeLabelStyles: SxProps<Theme> = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "170px", // 240px node width - 48px icon - 8px padding - 8px gap - 8px padding
    "& .validation-status": {
        display: "flex",
        alignItems: "baseline",
        gap: "4px"
    },
    "& .status-icon": { display: "none" },
    "& .validation-success": { color: "success.500" },
    "& .validation-failed": { color: "critical.500" }
};

export const hideHandleStyles = { visibility: "hidden" } as const;

const getArrowStyles = (isDeployed: boolean) => {
    return {
        bottom: {
            borderLeft: "5px solid transparent",
            borderRight: "5px solid transparent",
            borderTop: "7px solid",
            borderTopColor: isDeployed ? EDGE_COLORS.DEPLOYED : EDGE_COLORS.DEFAULT,
            borderBottom: 0,
            top: "-8px"
        },
        left: {
            borderLeft: "7px solid",
            borderLeftColor: isDeployed ? EDGE_COLORS.DEPLOYED : EDGE_COLORS.DEFAULT,
            borderRight: 0,
            borderTop: "5px solid transparent",
            borderBottom: "5px solid transparent",
            left: "-8px"
        }
    };
};

const getCircleStyles = (isDeployed: boolean) => {
    return {
        bottom: {
            border: "none",
            bottom: "-7px",
            background: isDeployed ? EDGE_COLORS.DEPLOYED : EDGE_COLORS.DEFAULT,
            width: "12px",
            borderRadius: "0px",
            borderBottomLeftRadius: "10px",
            borderBottomRightRadius: "10px"
        },
        right: {
            border: "none",
            right: "-7px",
            background: isDeployed ? EDGE_COLORS.DEPLOYED : EDGE_COLORS.DEFAULT,
            height: "12px",
            borderRadius: "0px",
            borderTopRightRadius: "10px",
            borderBottomRightRadius: "10px"
        }
    };
};

export const handleStyles = (position: Position, status: string) => {
    const isDeployed = status === "DEPLOYED" || status === "RUNNING";
    const ArrowStyles = getArrowStyles(isDeployed);
    const CircleStyles = getCircleStyles(isDeployed);
    return {
        "& .target-handle": {
            ...(position === Position.Bottom ? ArrowStyles.bottom : ArrowStyles.left),
            background: "none",
            borderRadius: 0
        },
        "& .source-handle": {
            ...(position === Position.Bottom ? CircleStyles.bottom : CircleStyles.right)
        }
    };
};

export const statStyle = (type: string, currentLayout: string): SxProps => {
    return {
        position: "absolute",
        backgroundColor: "#D8E4F0",
        color: "#0F0F42",
        borderRadius: "11px",
        padding: "0 7px",
        whiteSpace: "nowrap",
        ...(
            type === "STREAM"
                ? currentLayout === Layouts.HORIZONTAL
                    ? {top: "-26px", left: "-32px"}
                    : { top: "3px", left: "40px" }
                : { top: "-14px", right: "-8px" }
        )
    };
};

export const FlowIconStyles: SxProps = {
    width: 48,
    height: 48,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
    color: "#666"
};

export const errorIconStyles: SxProps = {
    position: "absolute",
    top: "-8px",
    right: "-8px",
    fontSize: "20px"
};

export const nodeIconStyles: SxProps = {
    background: "#EBF2FA",
    borderRadius: "8px",
    display: "flex",
    paddingLeft: "2px"
};

export const flowIconStyles: SxProps = {
    background: "#EBF2FA",
    borderRadius: "30px"
};
